import QRCodeSVG from "react-qr-code";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const QuestionQrCode = ({
    data,
    type,
    enableHideToggle = false,
}: {
    data: any,
    type: string,
    enableHideToggle?: boolean,
}) => {
    const { t } = useTranslation();
    const environment = import.meta.env.VITE_ENVIRONMENT;
    const [isQrHidden, setIsQrHidden] = useState(true);
    const [showQrPreview, setShowQrPreview] = useState(false);

    // Reset hidden state whenever data or type changes
    useEffect(() => {
        setIsQrHidden(true);
        setShowQrPreview(false);
    }, [data, type]);

    const getBaseUrl = () => {
        if (environment === "staging") return "https://staging.khoshmajlis.com";
        if (environment === "development") return "https://kfm.dev.excellentwebworld.co";
        if (environment === "production") return "https://khoshmajlis.com";
        if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
        return "https://staging.khoshmajlis.com";
    };

    const CDN_URL = import.meta.env.VITE_BASE_CDN_URL;

    const qrValue = type === "media"
        ? `${CDN_URL}/${data}`
        : (typeof data === "object" && data !== null
            ? `${getBaseUrl()}/qr?en=${encodeURIComponent(data?.en || "")}&ar=${encodeURIComponent(data?.ar || "")}`
            : `${CDN_URL}/${data}`);

    const wrapperClassName = "2xl:max-h-[45vh] xl:max-h-[45vh] lg:max-h-[45vh] h-full min-h-[170px] landscape:max-lg:max-h-[150px] flex-1 2xl:max-w-full xl:max-w-[500px] max-w-[400px] landscape:max-lg:max-w-[300px] 2xl:mt-[0.5vw] lg:mt-3 mt-1 w-full overflow-hidden flex items-center justify-center bg-white relative";
    const wordWrapperClassName = "2xl:max-h-[22vh] xl:max-h-[18vh] lg:max-h-[20vh] h-full min-h-[150px] landscape:max-lg:max-h-[150px] flex-1 2xl:max-w-full xl:max-w-[500px] max-w-[400px] landscape:max-lg:max-w-[300px] 2xl:mt-[0.5vw] lg:mt-3 mt-1 w-full overflow-hidden flex items-center justify-center bg-white relative";

    const handleQrClick = () => {
        if (isQrHidden) return;
        setShowQrPreview(true);
    };

    const renderQrContent = (containerClass: string) => (
        <div
            className={`${containerClass} ${isQrHidden ? "cursor-default" : "cursor-pointer"}`}
            onClick={enableHideToggle ? handleQrClick : undefined}
        >
            <div className={`w-full h-full flex items-center justify-center transition-all duration-300 ${isQrHidden ? "blur-sm select-none" : "blur-none"}`}>
                <QRCodeSVG
                    value={qrValue}
                    size={256}
                    style={{ height: "100%", maxWidth: "100%", width: "100%", objectFit: "contain" }}
                />
            </div>
        </div>
    );

    const renderModal = () => (
        showQrPreview && (
            <div
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-60 p-4"
                onClick={() => setShowQrPreview(false)}
                role="presentation"
            >
                <div
                    className="relative max-w-[min(90vw,720px)] max-h-[min(85dvh,720px)] p-2 bg-white-500 rounded-xl border-[1.5px] border-black-300 shadow-lg"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                >
                    <button
                        type="button"
                        onClick={() => setShowQrPreview(false)}
                        className="absolute -top-2 -end-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-yellow border-[1.5px] border-black-300 shadow-[0px_2px_0px_#110706] hover:scale-105 transition-transform"
                        aria-label="Close"
                    >
                        <span className="text-black-300 font-bold leading-none text-lg">×</span>
                    </button>
                    <QRCodeSVG
                        value={qrValue}
                        size={512}
                        style={{ maxWidth: "min(80dvh,680px)", width: "100%", height: "auto" }}
                        className="rounded-md"
                    />
                </div>
            </div>
        )
    );

    if (type === "media") {
        return (
            <div className="flex flex-col items-center justify-center w-full">
                {enableHideToggle && (
                    <button
                        type="button"
                        onClick={() => setIsQrHidden((prev) => !prev)}
                        className="my-2 skew-x-[-10deg] border-[1.5px] border-black-300 bg-primary-orange text-white font-bold px-3 py-1 rounded-[5px] shadow-[0px_2px_0px_#110706] hover:opacity-90 transition-opacity"
                    >
                        <span className="skew-x-10 inline-block">{isQrHidden ? t("qrCode.showQrCode") : t("qrCode.hideQrCode")}</span>
                    </button>
                )}
                {renderQrContent(wrapperClassName)}
                {renderModal()}
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center w-full">
            {enableHideToggle && (
                <button
                    type="button"
                    onClick={() => setIsQrHidden((prev) => !prev)}
                    className="my-2 skew-x-[-10deg] border-[1.5px] border-black-300 bg-primary-orange text-white font-bold px-3 py-1 rounded-[5px] shadow-[0px_2px_0px_#110706] hover:opacity-90 transition-opacity"
                >
                    <span className="skew-x-10 inline-block">{isQrHidden ? t("qrCode.showQrCode") : t("qrCode.hideQrCode")}</span>
                </button>
            )}
            {renderQrContent(wordWrapperClassName)}
            {renderModal()}
        </div>
    );
};

export default QuestionQrCode;
